import React, { useState, useMemo } from "react";
import { FaEdit, FaTrash, FaSlidersH, FaExternalLinkAlt } from "react-icons/fa";
import AccountsTable from "./AccountsTable";
import { EmailLink, PhoneLink } from "../components/ContactLinks";
import { phoneCallLink } from "../utils/phone";
import TablePagination, {
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  readStoredRowsPerPage,
} from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import useCompactViewport from "../hooks/useCompactViewport";
import "../assets/css/DigixLeads.css";

const ROWS_STORAGE_KEY = "vaultRowsPerPage";

/**
 * AccountsLeadsPage
 *
 * Self-contained list for Accounts: frontend filters, New Account button, global search, table, pagination.
 * UI: Row 1 = Account Name, Username, Clear (left) | New Account (right). Row 2 = Search... input.
 */
function AccountsLeadsPage({
  tenantFilter = null,
  accounts = [],
  customers = [],
  onViewAccount,
  onEditAccount,
  onDeleteAccount,
  onAddAccountClick,
}) {
  const [filters, setFilters] = useState({
    accountName: "",
    accountUsername: "",
    freeSearch: "",
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => readStoredRowsPerPage(ROWS_STORAGE_KEY));
  const [columnWidths, setColumnWidths] = useState({});
  const resizeRef = React.useRef({ startX: 0, startWidth: 0, columnIndex: null });
  const isCompactViewport = useCompactViewport();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ accountName: "", accountUsername: "", freeSearch: "" });
    setSortColumn(null);
    setSortDirection("asc");
    setCurrentPage(1);
  };

  const customersById = useMemo(() => {
    const map = new Map();
    (customers || []).forEach((c) => {
      const id = c?.id ?? c?.client_id ?? c?.client_id;
      const name =
        c?.name ??
        c?.Customer_Name ??
        c?.customer_name ??
        c?.companyName ??
        "";
      if (id != null && String(name).trim() !== "") {
        map.set(String(id), String(name));
      }
    });
    return map;
  }, [customers]);

  const baseAccounts = useMemo(() => {
    return (accounts || []).map((a) => {
      const customerId = a?.clientId ?? a?.client_id ?? a?.client_id ?? a?.client?.client_id;
      const fromApi =
        a?.customerName ??
        a?.customer_name ??
        a?.Customer_Name ??
        a?.client?.Customer_Name ??
        a?.client?.customer_name ??
        "";
      const fromCustomers = customerId != null ? customersById.get(String(customerId)) : undefined;
      const finalName = (String(fromApi).trim() !== "" && fromApi !== "—") ? fromApi : (fromCustomers || "—");
      return {
        ...a,
        customerId: customerId ?? a?.clientId ?? a?.client_id ?? null,
        customerName: finalName,
        customer_name: finalName
      };
    });
  }, [accounts, customersById]);

  const fieldFilteredAccounts = useMemo(() => {
    const accountNameFilter = String(filters.accountName || "").trim().toLowerCase();
    const usernameFilter = String(filters.accountUsername || "").trim().toLowerCase();
    if (!accountNameFilter && !usernameFilter) return baseAccounts;

    return baseAccounts.filter((account) => {
      const accountName = String(account.accountName ?? account.account_name ?? "").toLowerCase();
      const username = String(account.username ?? "").toLowerCase();
      const nameOk = !accountNameFilter || accountName.includes(accountNameFilter);
      const userOk = !usernameFilter || username.includes(usernameFilter);
      return nameOk && userOk;
    });
  }, [baseAccounts, filters.accountName, filters.accountUsername]);

  // Free search across key account fields (frontend, on type)
  const freeSearchFiltered = useMemo(() => {
    const searchValue = String(filters.freeSearch || "").toLowerCase().trim();
    if (!searchValue) return fieldFilteredAccounts;

    return fieldFilteredAccounts.filter((account) => {
      const accountName = String(
        account.accountName ?? account.account_name ?? ""
      )
        .trim()
        .toLowerCase();
      const username = String(account.username ?? "").trim().toLowerCase();
      const password = String(account.password ?? "").trim().toLowerCase();
      const businessUrl = String(
        account.businessUrl ?? account.business_url ?? ""
      )
        .trim()
        .toLowerCase();
      const profileUrl = String(
        account.profileUrl ?? account.profile_url ?? ""
      )
        .trim()
        .toLowerCase();
      const contact = String(
        account.contact ??
          account.contact_name ??
          account.contactPhone ??
          account.contact_phone ??
          ""
      )
        .trim()
        .toLowerCase();
      const customerName = String(
        account.customerName ?? account.customer_name ?? ""
      )
        .trim()
        .toLowerCase();
      const platform = String(account.platform ?? "").trim().toLowerCase();
      const status = String(
        account.status ??
          account.Status ??
          account.account_status ??
          account.Account_Status ??
          ""
      )
        .trim()
        .toLowerCase();

      return (
        accountName.includes(searchValue) ||
        username.includes(searchValue) ||
        password.includes(searchValue) ||
        businessUrl.includes(searchValue) ||
        profileUrl.includes(searchValue) ||
        contact.includes(searchValue) ||
        customerName.includes(searchValue) ||
        platform.includes(searchValue) ||
        status.includes(searchValue)
      );
    });
  }, [fieldFilteredAccounts, filters.freeSearch]);

  const displayedAccounts = useMemo(() => {
    if (!sortColumn) return freeSearchFiltered;
    const sortValue = (account, columnId) => {
      switch (columnId) {
        case "accountName":
          return String(account.accountName ?? account.account_name ?? "").toLowerCase();
        case "username":
          return String(account.username ?? "").toLowerCase();
        case "url":
          return String(account.profileUrl ?? account.profile_url ?? "").toLowerCase();
        case "email":
          return String(account.emailUsed ?? account.email_used ?? "").toLowerCase();
        case "contact":
          return String(
            account.contact ??
              account.contact_name ??
              account.contactPhone ??
              account.contact_phone ??
              ""
          ).toLowerCase();
        case "notes":
          return String(account.notes ?? "").toLowerCase();
        case "platform":
          return String(account.platform ?? "").toLowerCase();
        default:
          return "";
      }
    };
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFiltered].sort((a, b) => {
      const va = sortValue(a, sortColumn);
      const vb = sortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a.id ?? a.account_id ?? 0);
      const idb = Number(b.id ?? b.account_id ?? 0);
      return (ida - idb) * mult;
    });
  }, [freeSearchFiltered, sortColumn, sortDirection]);

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const totalPages = Math.max(1, Math.ceil(displayedAccounts.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const currentRows = displayedAccounts.slice(startIndex, startIndex + rowsPerPage);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    try {
      localStorage.setItem(ROWS_STORAGE_KEY, String(next));
    } catch {
      /* ignore */
    }
  };

  const getColumnWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) {
      return `${columnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const getColumnMinWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) {
      return `${columnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.parentElement;
    if (!th) return;

    const startWidth = th.offsetWidth;
    const startX = e.clientX;

    resizeRef.current = {
      startX,
      startWidth,
      columnIndex
    };

    document.body.classList.add("resizing");

    const handleMouseMove = (moveEvent) => {
      if (resizeRef.current.columnIndex === null) return;
      const diff = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = Math.max(80, resizeRef.current.startWidth + diff);

      setColumnWidths((prev) => ({
        ...prev,
        [resizeRef.current.columnIndex]: newWidth
      }));
    };

    const handleMouseUp = () => {
      resizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove("resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div className="accounts-page">
      <div className={`search-actions-row co-filters-collapsible${filtersOpen || !isCompactViewport ? " is-open" : ""}`}>
        {isCompactViewport ? (
          <div className="co-filter-search-bar">
            <input
              type="text"
              className="co-filter-search-bar__input"
              placeholder="Search account name..."
              aria-label="Search account name"
              value={filters.accountName}
              onChange={(e) => handleFilterChange("accountName", e.target.value)}
            />
            <button
              type="button"
              className={`co-filter-search-bar__icon${filtersOpen ? " is-active" : ""}`}
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              aria-label={filtersOpen ? "Hide Filters" : "Show Filters"}
              title={filtersOpen ? "Hide Filters" : "Show Filters"}
            >
              <FaSlidersH aria-hidden />
            </button>
          </div>
        ) : null}
        <div className="co-filters-body">
          <div className="search-filters-row">
            {tenantFilter}
            {!isCompactViewport ? (
              <input
                type="text"
                className="search-filter-input"
                placeholder="Search account name..."
                value={filters.accountName}
                onChange={(e) => handleFilterChange("accountName", e.target.value)}
              />
            ) : null}
            <input
              type="text"
              className="search-filter-input"
              placeholder="Search username..."
              value={filters.accountUsername}
              onChange={(e) => handleFilterChange("accountUsername", e.target.value)}
            />
          </div>
        </div>
        <div className="co-filter-actions">
          <button type="button" className="btn-clear" onClick={handleClearFilters}>
            Clear
          </button>
          {onAddAccountClick ? (
            <button type="button" className="btn-add-lead" onClick={onAddAccountClick}>
              New Account
            </button>
          ) : null}
        </div>
      </div>

      <div className="search-row-2 co-results-sticky">
        <div className="free-search-bar">
          <input
            type="text"
            className="free-search-input"
            placeholder="Search..."
            value={filters.freeSearch}
            onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
            title="Search across all columns"
          />
        </div>
      </div>

      <div className="table-wrapper co-desktop-table">
        <div className="table-container">
          <AccountsTable
            rows={currentRows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            getColumnWidth={getColumnWidth}
            getColumnMinWidth={getColumnMinWidth}
            handleResizeStart={handleResizeStart}
            handleView={onViewAccount}
            handleEdit={onEditAccount}
            handleDelete={onDeleteAccount}
          />
        </div>
      </div>

      <ResultsMobileList emptyLabel="No accounts found.">
        {currentRows.map((account) => {
          const id = account.id || account.account_id;
          const name = account.accountName || account.account_name || "—";
          const username = account.username || null;
          const platform = account.platform || null;
          const rawEmail = account.emailUsed || account.email_used;
          const email = rawEmail && rawEmail !== "—" ? <EmailLink email={rawEmail} /> : null;
          const rawContact = account.contact || account.contact_phone;
          const contact = phoneCallLink(account.countryCode, rawContact) ? (
            <PhoneLink countryCode={account.countryCode} number={rawContact} />
          ) : null;
          const profileUrl = account.profileUrl || account.profile_url || null;
          const customer = account.customerName || account.customer_name || null;

          return (
            <ResultsMobileCard
              key={id || name}
              title={name}
              subtitle={username ? `@${username}` : null}
              meta={[platform, customer].filter(Boolean)}
              fields={[
                { label: "Email", value: email },
                { label: "Contact", value: contact },
                { label: "URL", value: profileUrl },
              ]}
              onOpen={typeof onViewAccount === "function" ? () => onViewAccount(id) : undefined}
              actions={
                <div className="co-mcard__action-grid">
                  {profileUrl ? (
                    <a
                      className="co-maction"
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open URL"
                      aria-label="Open URL"
                    >
                      <FaExternalLinkAlt size={13} />
                      <span>Open</span>
                    </a>
                  ) : null}
                  {typeof onEditAccount === "function" ? (
                    <button
                      type="button"
                      className="co-maction"
                      title="Edit Account"
                      aria-label="Edit Account"
                      onClick={() => onEditAccount(id)}
                    >
                      <FaEdit size={14} />
                      <span>Edit</span>
                    </button>
                  ) : null}
                  {typeof onDeleteAccount === "function" ? (
                    <button
                      type="button"
                      className="co-maction co-maction--danger"
                      title="Delete Account"
                      aria-label="Delete Account"
                      onClick={() => onDeleteAccount(id)}
                    >
                      <FaTrash size={14} />
                      <span>Delete</span>
                    </button>
                  ) : null}
                </div>
              }
            />
          );
        })}
      </ResultsMobileList>

      <TablePagination
        currentPage={safePage}
        totalItems={displayedAccounts.length}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        itemLabel="items"
        rowsPerPageSelectId="vault-rows-per-page"
      />
    </div>
  );
}

export default AccountsLeadsPage;

